/**
 * @fileoverview Banner Notification System (Section 3 Canonical)
 *
 * Single canonical banner system. All banners come from backend UI_UPDATE.
 * The enforcement orchestrator is the only source of banner firing.
 *
 * RIT2-97: Banner Notifications
 *
 * @module components/BannerNotification
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { decodeHtmlEntities } from '../utils/displayText';

// ============================================
// CANONICAL BANNER TYPES (matches backend)
// ============================================

export const CANONICAL_BANNER_TYPES = {
  interrupt_warning: 'interrupt_warning',
  soft_mute: 'soft_mute',
  hard_mute: 'hard_mute',
  tangent: 'tangent',
  invite_to_speak: 'invite_to_speak',
  overtalk_nudge: 'overtalk_nudge',
  agenda_wrapup_nudge: 'agenda_wrapup_nudge',
  agenda_time_warning: 'agenda_time_warning',
  agenda_advance_recommendation: 'agenda_advance_recommendation',
  decision_required: 'decision_required',
  time_critical: 'time_critical',
  topic_transition: 'topic_transition',
  unmute: 'unmute',
  discussion_parked: 'discussion_parked',
  info: 'info',
  insight_candidate_detected: 'insight_candidate_detected',
  insight_confirmed: 'insight_confirmed',
  insight_rejected: 'insight_rejected',
  /** Immediate feedback when user invokes "Hey Rithmo" (before LLM response) */
  thinking: 'thinking',
  /** Meeting lifecycle banners (auto-start countdown + start confirmation) */
  meeting_starting_soon: 'meeting_starting_soon',
  meeting_started: 'meeting_started',
  /** Voice suppression state (Rithmo muted/unmuted). */
  voice_suppressed: 'voice_suppressed',
  voice_unsuppressed: 'voice_unsuppressed',
  /** Silent signals — consumed by screenshare UI only; never render a visible pill. */
  screenshare_outputs_scroll_down: 'screenshare_outputs_scroll_down',
  screenshare_outputs_scroll_up: 'screenshare_outputs_scroll_up',
} as const;

export type CanonicalBannerType = (typeof CANONICAL_BANNER_TYPES)[keyof typeof CANONICAL_BANNER_TYPES];

/** Strict check: only canonical types render. No fallback to info. */
export function isCanonicalBannerType(type: string): type is CanonicalBannerType {
  return Object.values(CANONICAL_BANNER_TYPES).includes(type as CanonicalBannerType);
}

/** Log key for structured error logging (matches backend LOG_KEYS.UI_BANNER_UNKNOWN_TYPE) */
export const BANNER_LOG_KEYS = { UI_BANNER_UNKNOWN_TYPE: 'ui_banner_unknown_type' } as const;

// ============================================
// BANNER ICONS
// ============================================

const BANNER_ICONS: Record<string, string> = {
  [CANONICAL_BANNER_TYPES.interrupt_warning]: '✋',
  [CANONICAL_BANNER_TYPES.soft_mute]: '🔇',
  [CANONICAL_BANNER_TYPES.hard_mute]: '🔇',
  [CANONICAL_BANNER_TYPES.tangent]: '📋',
  [CANONICAL_BANNER_TYPES.invite_to_speak]: '🎤',
  [CANONICAL_BANNER_TYPES.overtalk_nudge]: '⚡',
  [CANONICAL_BANNER_TYPES.agenda_wrapup_nudge]: '⏰',
  [CANONICAL_BANNER_TYPES.agenda_time_warning]: '⏰',
  [CANONICAL_BANNER_TYPES.agenda_advance_recommendation]: '⏰',
  [CANONICAL_BANNER_TYPES.decision_required]: '📋',
  [CANONICAL_BANNER_TYPES.time_critical]: '🚨',
  [CANONICAL_BANNER_TYPES.topic_transition]: '📋',
  [CANONICAL_BANNER_TYPES.unmute]: '🔊',
  [CANONICAL_BANNER_TYPES.discussion_parked]: '📌',
  [CANONICAL_BANNER_TYPES.info]: 'ℹ️',
  [CANONICAL_BANNER_TYPES.insight_candidate_detected]: '✅',
  [CANONICAL_BANNER_TYPES.insight_confirmed]: '✅',
  [CANONICAL_BANNER_TYPES.insight_rejected]: 'ℹ️',
  [CANONICAL_BANNER_TYPES.thinking]: '💭',
  [CANONICAL_BANNER_TYPES.meeting_starting_soon]: '🟡',
  [CANONICAL_BANNER_TYPES.meeting_started]: '🟢',
};

// ============================================
// DEFAULT AUTO-DISMISS TIMES (ms)
// ============================================

const DEFAULT_DISMISS_TIMES: Record<string, number> = {
  [CANONICAL_BANNER_TYPES.interrupt_warning]: 5000,
  [CANONICAL_BANNER_TYPES.soft_mute]: 10000,
  [CANONICAL_BANNER_TYPES.hard_mute]: 8000,
  [CANONICAL_BANNER_TYPES.tangent]: 6000,
  [CANONICAL_BANNER_TYPES.invite_to_speak]: 6000,
  [CANONICAL_BANNER_TYPES.overtalk_nudge]: 5000,
  [CANONICAL_BANNER_TYPES.agenda_wrapup_nudge]: 5000,
  [CANONICAL_BANNER_TYPES.agenda_time_warning]: 5000,
  [CANONICAL_BANNER_TYPES.agenda_advance_recommendation]: 8000,
  [CANONICAL_BANNER_TYPES.decision_required]: 6000,
  [CANONICAL_BANNER_TYPES.time_critical]: 8000,
  [CANONICAL_BANNER_TYPES.topic_transition]: 6000,
  [CANONICAL_BANNER_TYPES.unmute]: 4000,
  [CANONICAL_BANNER_TYPES.discussion_parked]: 5000,
  [CANONICAL_BANNER_TYPES.info]: 4000,
  [CANONICAL_BANNER_TYPES.insight_candidate_detected]: 5000,
  [CANONICAL_BANNER_TYPES.insight_confirmed]: 4000,
  [CANONICAL_BANNER_TYPES.insight_rejected]: 4000,
  [CANONICAL_BANNER_TYPES.thinking]: 30000,
  [CANONICAL_BANNER_TYPES.meeting_starting_soon]: 10000,
  [CANONICAL_BANNER_TYPES.meeting_started]: 8000,
};

export type BannerPayload = {
  id: string;
  type: CanonicalBannerType | string;
  title?: string;
  message?: string;
  autoDismiss?: boolean;
  dismissTime?: number;
  showProgress?: boolean;
  receivedAt: number;
};

export type NewBannerPayload = Omit<BannerPayload, 'id'>;

// CSS class: use type as-is (e.g. soft_mute); CSS supports underscore in class names
function typeToCssClass(type: string): string {
  return type.replace(/_/g, '-');
}

// ============================================
// SINGLE BANNER COMPONENT
// ============================================

type BannerNotificationProps = BannerPayload & {
  onDismiss?: (id: string) => void;
};

function BannerNotification({
  id,
  type,
  title,
  message,
  autoDismiss = true,
  dismissTime,
  onDismiss,
  showProgress = true,
}: BannerNotificationProps) {
  if (!type || !isCanonicalBannerType(type)) {
    return null;
  }

  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const actualDismissTime = dismissTime ?? DEFAULT_DISMISS_TIMES[type] ?? 5000;

  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss?.(id);
    }, 300);
  }, [id, onDismiss]);

  useEffect(() => {
    if (!autoDismiss) return undefined;

    startTimeRef.current = Date.now();

    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, 100 - (elapsed / actualDismissTime) * 100);
      setProgress(remaining);
    }, 50);

    timerRef.current = setTimeout(() => {
      handleDismiss();
    }, actualDismissTime);

    return () => {
      clearInterval(progressInterval);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [autoDismiss, actualDismissTime, handleDismiss]);

  const icon = BANNER_ICONS[type];
  const cssClass = typeToCssClass(type);
  const line = (() => {
    const m = typeof message === 'string' && message.trim() !== '' ? message.trim() : '';
    if (m) return m;
    const t = typeof title === 'string' && title.trim() !== '' ? title.trim() : '';
    return t;
  })();

  return (
    <div
      className={`banner-notification ${cssClass} ${isExiting ? 'exiting' : ''}`}
      role="alert"
      aria-live="polite"
    >
      <span className="banner-icon">{icon}</span>
      <div className="banner-content">
        {line ? <div className="banner-message">{decodeHtmlEntities(line)}</div> : null}
      </div>
      <button
        className="banner-dismiss"
        onClick={handleDismiss}
        aria-label="Dismiss notification"
      >
        ✕
      </button>
      {autoDismiss && showProgress && (
        <div
          className="banner-progress"
          style={{ width: `${progress}%` }}
        />
      )}
    </div>
  );
}

// ============================================
// BANNER CONTAINER
// ============================================

type BannerNotificationContainerProps = {
  banners: BannerPayload[];
  onDismiss: (id: string) => void;
};

export function BannerNotificationContainer({ banners, onDismiss }: BannerNotificationContainerProps) {
  if (!banners || banners.length === 0) return null;

  const canonicalBanners = banners.filter((b) => b.type && isCanonicalBannerType(b.type));

  return (
    <div className="banner-notification-container">
      {canonicalBanners.map((banner) => (
        <BannerNotification
          key={banner.id}
          {...banner}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  );
}

// ============================================
// BANNER HOOK (canonical only – addBanner from UI_UPDATE)
// ============================================

let bannerId = 0;

export type BannerState = {
  banners: BannerPayload[];
  addBanner: (banner: NewBannerPayload) => string;
  removeBanner: (id: string) => void;
  removeBannersByType: (type: string) => void;
  clearAll: () => void;
};

export function useBannerNotifications(maxBanners = 3): BannerState {
  const [banners, setBanners] = useState<BannerPayload[]>([]);

  const addBanner = useCallback((banner: NewBannerPayload) => {
    const id = `banner-${++bannerId}`;
    const newBanner: BannerPayload = { id, receivedAt: Date.now(), ...banner };

    setBanners((prev) => {
      const updated = prev.length >= maxBanners ? prev.slice(1) : prev;
      return [...updated, newBanner];
    });

    return id;
  }, [maxBanners]);

  const removeBanner = useCallback((id: string) => {
    setBanners((prev) => prev.filter((b) => b.id !== id));
  }, []);

  const removeBannersByType = useCallback((type: string) => {
    setBanners((prev) => prev.filter((b) => b.type !== type));
  }, []);

  const clearAll = useCallback(() => {
    setBanners([]);
  }, []);

  return {
    banners,
    addBanner,
    removeBanner,
    removeBannersByType,
    clearAll,
  };
}

// ============================================
// BANNER CONTEXT
// ============================================

const BannerContext = React.createContext<BannerState | null>(null);

type BannerProviderProps = {
  children: React.ReactNode;
  maxBanners?: number;
};

/**
 * Same gates as `MainView` screenshare agenda (`shareCapture=1&shareView=screenshare&meetingId`).
 * Synchronous read — used so the fixed global banner stack never mounts on first paint in capture mode
 * (otherwise `position:fixed` banners flash over the end-of-meeting recap outputs before `useEffect` runs).
 */
export function isScreenshareAudienceCaptureUrl(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get('shareCapture') === '1' &&
      params.get('shareView') === 'screenshare' &&
      Boolean(params.get('meetingId')?.trim())
    );
  } catch {
    return false;
  }
}

/**
 * Banner context only. The floating `BannerNotificationContainer` is rendered by
 * {@link BannerFloatingLayer} inside `MeetingStateProvider` so it can use a synchronous URL gate
 * (see `isScreenshareAudienceCaptureUrl`).
 */
export function BannerProvider({ children, maxBanners = 3 }: BannerProviderProps) {
  const bannerState = useBannerNotifications(maxBanners);

  return (
    <BannerContext.Provider value={bannerState}>
      {children}
    </BannerContext.Provider>
  );
}

export function useBanners(): BannerState {
  const context = React.useContext(BannerContext);
  if (!context) {
    throw new Error('useBanners must be used within a BannerProvider');
  }
  return context;
}

export default BannerNotificationContainer;
